import React, { useState, useEffect, useRef } from "react";
import { saveOrUpdateTemplate } from "../../utils/Utility";
import Select from "react-select";
import { selectStyles } from "../Constants";
import { useUser } from "../../contexts/UserContext";
import { useAdminContext } from "../../contexts/AdminContext.js";
import { DEFAULT_INVOICE_DATA, DEFAULT_CHARGES_DATA } from "../Constants";
import Loader from "../../utils/Loader";
import QRAlignmentModal from "./QRAlignmentModal.js";
import { formatOldUrl, decryptData } from "../../utils/cloudApi";
import HeaderColorPicker from "./HeaderColorPicker.js";
import ImagePickerModal from "../../utils/ImagePicker.js";

const EditTemplateModal = ({
  template,
  isOpen,
  onClose,
  onSave,
  invoiceCategory,
}) => {
  const { user, getRecipients, privateKeyInMemory } = useUser();
  const { isViewingOtherUser, selectedUserData } = useAdminContext();
  const currentUser = isViewingOtherUser ? selectedUserData : user;
  const defaultData =
    invoiceCategory === "noc" ? DEFAULT_CHARGES_DATA : DEFAULT_INVOICE_DATA;

  const [header, setHeader] = useState(defaultData.header);
  const [headerDescription, setHeaderDescription] = useState(
    defaultData.headerDescription
  );
  const [headerColor, setHeaderColor] = useState(defaultData.headerColor);
  const [tableHeaderColor, setTableHeaderColor] = useState(
    defaultData.tableHeaderColor
  );
  const [tableHeaderTextColor, setTableHeaderTextColor] = useState(
    defaultData.tableHeaderTextColor
  );

  const [decryptedTemplate, setDecryptedTemplate] = useState({});
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const [showQRAlignmentModal, setShowQRAlignmentModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [logo, setLogo] = useState("");
  const [logoDims, setLogoDims] = useState({
    width: 0,
    height: 0,
  });
  const [vatOption, setVatOption] = useState(defaultData.vatOption);
  const [vatMethod, setVatMethod] = useState(defaultData.vatMethod);
  const [taxCode, setTaxCode] = useState(defaultData.taxCode);
  const [vatRate, setVatRate] = useState(defaultData.vatRate);
  const [from, setFrom] = useState(defaultData.from);
  const [cash, setCash] = useState("");
  const [check, setCheck] = useState("");
  const [qr, setQr] = useState(null);
  const [qrDims, setQrDims] = useState({
    width: 0,
    height: 0,
  });
  const [qr2, setQr2] = useState(null);
  const [qr2Dims, setQr2Dims] = useState({
    width: 0,
    height: 0,
  });

  const [qrAlignment, setQrAlignment] = useState({});
  const [alignedQRs, setAlignedQRs] = useState(null);
  const [lastInvoiceNumber, setLastInvoiceNumber] = useState(0);
  const logoInputRef = useRef(null);
  const gcashQrInputRef = useRef(null);
  const gcashQr2InputRef = useRef(null);
  const cashDetailsRef = useRef(null);
  const checkDetailsRef = useRef(null);
  const [previews, setPreviews] = useState({
    logoPreview: null,
    qrPreview: null,
    qrPreview2: null,
    alignedQRsPreview: null,
  });
  const [showAlignmentPrompt, setShowAlignmentPrompt] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [currentImageField, setCurrentImageField] = useState(null);

  const vatOptions = [
    { value: "tax", label: "Tax" },
    { value: "non-vat", label: "Non-VAT" },
    { value: "vat", label: "VAT" },
  ];

  const vatMethodOptions = [
    {
      value: "inclusive",
      label: vatOption === "tax" ? "Tax Inclusive" : "VAT Inclusive",
    },
    {
      value: "exclusive",
      label: vatOption === "tax" ? "Tax Exclusive" : "VAT Exclusive",
    },
  ];

  useEffect(() => {
    const loadTemplate = async () => {
      if (template) {
        const decrypted = await decryptData(
          template,
          user.uid,
          privateKeyInMemory
        );
        setHeader(decrypted.header || defaultData.header);
        setHeaderDescription(
          decryptData.headerDescription || defaultData.headerDescription
        );
        setLogo(decrypted.logo || null);
        setVatOption(decrypted.vatOption || "vat");
        setVatMethod(decrypted.vatMethod || defaultData.vatMethod);
        setVatRate(decrypted?.vatRate || 12);
        setTaxCode(decrypted?.taxCode || "");
        setFrom(decrypted.from || { name: "", address: "", contact: "" });
        setCash(decrypted.cashDetails || "");
        setCheck(decrypted.checkDetails || "");
        setQr(decrypted.gcashQr || null);
        setQr2(decrypted.gcashQr2 || null);
        setLastInvoiceNumber(decrypted.lastInvoiceNumber || 0);
        setAlignedQRs(decrypted.alignedQRs || null);
        setQrAlignment(decrypted.qrAlignment || null);
        setTableHeaderColor(decrypted.tableHeaderColor || [43, 112, 115]);
        setTableHeaderTextColor(
          decrypted.tableHeaderTextColor || [255, 255, 255]
        );
        setHeaderColor(decrypted.headerColor || [0, 0, 0]);

        setDecryptedTemplate(decrypted);

        // Trigger resizing after values are set
        setTimeout(() => {
          resizeTextarea(cashDetailsRef);
          resizeTextarea(checkDetailsRef);
        }, 0);
        setTemplateLoaded(true);
      }
    };

    loadTemplate();
  }, [template, defaultData, user]);

  useEffect(() => {
    resizeTextarea(cashDetailsRef);
    resizeTextarea(checkDetailsRef);
  }, [cash, check]);

  useEffect(() => {
    if (templateLoaded && (!qr || !qr2)) {
      setAlignedQRs(null);
      setPreviews((prev) => ({ ...prev, alignedQRsPreview: null }));
      setQrAlignment(null);
    }
  }, [qr, qr2, templateLoaded]);

  const resizeTextarea = (ref) => {
    if (ref.current) {
      ref.current.style.height = "auto";
      ref.current.style.height = `${ref.current.scrollHeight}px`;
    }
  };

  const handleSave = async () => {
    const hasBothQRs = qr && qr2;
    const hasNoAlignment = !alignedQRs && !qrAlignment;

    if (hasBothQRs && hasNoAlignment) {
      setShowAlignmentPrompt(true);
      return;
    }

    setLoading(true);
    setLoadingText("Saving template...");

    try {
      const tempType = invoiceCategory === "noc" ? "noc" : "inv";

      // save the new files and save to firebase through utility function first
      const updatedTemplate = {
        ...decryptedTemplate,
        header,
        headerDescription,
        vatOption: vatOption,
        vatMethod: vatMethod,
        taxCode: taxCode,
        vatRate: vatRate,
        from,
        createdBy: currentUser.uid,
        checkDetails: check,
        cashDetails: cash,
        gcashQr: qr,
        gcashQr2: qr2,
        logo,
        invoiceNo: lastInvoiceNumber,
        alignedQRs: alignedQRs || null,
        qrAlignment: qrAlignment || null,
        headerColor: headerColor,
        tableHeaderColor: tableHeaderColor,
        tableHeaderTextColor: tableHeaderTextColor,
        tempType: tempType,
      };

      const recipients = await getRecipients();

      let newTemplate;
      if (!decryptedTemplate || !decryptedTemplate.id) {
        // new template
        newTemplate = await saveOrUpdateTemplate(
          null,
          updatedTemplate,
          false,
          null,
          recipients
        );
      } else {
        // update template
        newTemplate = await saveOrUpdateTemplate(
          decryptedTemplate.id,
          updatedTemplate,
          true,
          null,
          recipients
        );
      }

      onSave(newTemplate);
      onClose();
    } catch (error) {
      console.error("Error saving template:", error);
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  const handleHeaderColorChange = (key, rgb) => {
    if (key === "header") setHeaderColor(rgb);
    else if (key === "tableHeader") setTableHeaderColor(rgb);
    else if (key === "tableHeaderText") setTableHeaderTextColor(rgb);
  };

  const handleImageFieldClick = (field) => {
    setCurrentImageField(field);
    setImagePickerOpen(true);
  };

  const handleImageSelect = (selectedValue) => {
    // selectedValue can be either a File object (new upload) or a URL string (existing image)
    const isFile = selectedValue instanceof File;

    // console.log("selected value: ", selectedValue);

    if (isFile) {
      // New file upload
      const previewUrl = URL.createObjectURL(selectedValue);

      // console.log("Preview url: ", previewUrl);
      // console.log("selected value: ", selectedValue);

      if (currentImageField === "logo") {
        if (previews.logoPreview?.startsWith("blob:")) {
          URL.revokeObjectURL(previews.logoPreview);
        }
        setPreviews((prev) => ({ ...prev, logoPreview: previewUrl }));
        setLogo(selectedValue);
      } else if (currentImageField === "gcashQr") {
        if (previews.qrPreview?.startsWith("blob:")) {
          URL.revokeObjectURL(previews.qrPreview);
        }
        setPreviews((prev) => ({ ...prev, qrPreview: previewUrl }));
        setQr(selectedValue);
      } else if (currentImageField === "gcashQr2") {
        if (previews.qrPreview2?.startsWith("blob:")) {
          URL.revokeObjectURL(previews.qrPreview2);
        }
        setPreviews((prev) => ({ ...prev, qrPreview2: previewUrl }));
        setQr2(selectedValue);
      }
    } else {
      // Existing image URL - just use the URL, no file upload needed
      if (currentImageField === "logo") {
        setPreviews((prev) => ({ ...prev, logoPreview: selectedValue }));
        setLogo(selectedValue); // Store URL instead of file
      } else if (currentImageField === "gcashQr") {
        setPreviews((prev) => ({ ...prev, qrPreview: selectedValue }));
        setQr(selectedValue);
      } else if (currentImageField === "gcashQr2") {
        setPreviews((prev) => ({ ...prev, qrPreview2: selectedValue }));
        setQr2(selectedValue);
      }
    }
  };

  const dataURLtoFile = (dataUrl, filename) => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) {
      throw new Error("Invalid data URL");
    }
    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const handleAlignFromPrompt = () => {
    setShowAlignmentPrompt(false);
    setShowQRAlignmentModal(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 py-6 flex items-center justify-center z-40">
      {loading && <Loader text={loadingText} />}

      {showAlignmentPrompt && (
        <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-tier-primary dark:bg-tier-primary-dark border dark:border-gray-600 rounded-lg p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <span className="material-symbols-outlined text-yellow-500 text-2xl">
                warning
              </span>
              <h3 className="text-lg font-semibold text-white">
                QR Code Alignment Recommended
              </h3>
            </div>

            <p className="text-gray-300 mb-6">
              You have uploaded two QR codes but haven't aligned them yet. For
              better presentation in your invoice it is recommended to align
              them first.
            </p>

            <div className="flex gap-3 justify-end">
              <button onClick={handleAlignFromPrompt} className="btn-action">
                Align QR Codes
              </button>
            </div>
          </div>
        </div>
      )}

      {imagePickerOpen && (
        <ImagePickerModal
          isOpen={imagePickerOpen}
          onClose={() => setImagePickerOpen(false)}
          onSelect={handleImageSelect}
          userId={currentUser.uid}
          currentImageUrl={
            currentImageField === "logo"
              ? logo
              : currentImageField === "gcashQr"
              ? qr
              : qr2
          }
        />
      )}

      {showQRAlignmentModal && (
        <QRAlignmentModal
          isOpen={showQRAlignmentModal}
          onClose={() => setShowQRAlignmentModal(false)}
          src1={
            previews.qrPreview
              ? previews.qrPreview
              : qr
              ? formatOldUrl(qr)
              : null
          }
          src2={
            previews.qrPreview2
              ? previews.qrPreview2
              : qr2
              ? formatOldUrl(qr2)
              : null
          }
          initialAlignment={qrAlignment} // Pass saved alignment data
          onComplete={(alignedImageDataUrl, alignmentData) => {
            const file = dataURLtoFile(
              alignedImageDataUrl,
              "aligned-qr-codes.png"
            );

            // Update preview
            setPreviews((prev) => ({
              ...prev,
              alignedQRsPreview: alignedImageDataUrl,
            }));

            setAlignedQRs(file);
            setQrAlignment(alignmentData);

            setShowQRAlignmentModal(false);
          }}
        />
      )}

      <div className="bg-tier-primary dark:bg-tier-primary-dark dark:border-gray-600 border text-color rounded-lg shadow-xl w-full max-w-4xl h-full flex flex-col">
        <h2 className="text-2xl font-bold px-8 pt-8">
          {!decryptedTemplate || !decryptedTemplate.id ? "New " : "Edit "}
          {invoiceCategory === "noc" ? "Notice of Charges" : "Invoice"} Template
        </h2>

        <div className="custom-divider"></div>

        <div className="flex-1 overflow-y-auto p-8 space-y-6">
          {/* Template Header */}
          <div className="flex flex-row w-full">
            <div className="flex-1 px-4">
              <label htmlFor="header" className="block text-sm">
                Document Title
              </label>
              <input
                id="header"
                type="text"
                value={header}
                onChange={(e) => setHeader(e.target.value)}
                className="w-full p-3 border rounded-lg custom-input outline-none"
              />
            </div>
            <div className="flex-full px-4">
              <label htmlFor="invoiceNo" className="block text-sm">
                Current Invoice No
              </label>
              <input
                id="invoiceNo"
                type="number"
                value={lastInvoiceNumber}
                onChange={(e) =>
                  setLastInvoiceNumber(
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className="w-full p-3 border rounded-lg custom-input outline-none"
              />
            </div>
          </div>

          <div className="flex-1 px-4">
            <label htmlFor="header-description" className="block text-sm">
              Description / Subtitle
            </label>
            <input
              id="header-description"
              type="text"
              value={headerDescription}
              onChange={(e) => setHeaderDescription(e.target.value)}
              className="w-full p-3 border rounded-lg custom-input outline-none"
            />
          </div>

          <div className="p-4 bg-white/10 rounded-xl">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              Company Text Color
            </h2>

            <HeaderColorPicker
              defaultHex={headerColor}
              defaultText={"This will be the text color of the company name"}
              onChange={(rgb) => handleHeaderColorChange("header", rgb)}
            />
          </div>

          <div className="p-4 bg-white/10 rounded-xl">
            <h2 className="text-lg font-bold mb-4">Table Header Color</h2>

            <div className="flex justify-between">
              <HeaderColorPicker
                defaultHex={tableHeaderColor}
                defaultText={"This will be the color of the table header"}
                onChange={(rgb) => handleHeaderColorChange("tableHeader", rgb)}
              />

              <HeaderColorPicker
                defaultHex={tableHeaderTextColor}
                defaultText={
                  "This will be the text color of the column names in the table header"
                }
                onChange={(rgb) =>
                  handleHeaderColorChange("tableHeaderText", rgb)
                }
              />
            </div>
          </div>

          <div className="bg-white/10 p-4 rounded-lg space-y-4">
            <h2 className="font-bold text-lg">Tax Options</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="vatOption" className="block text-sm">
                  Invoice Type
                </label>
                <Select
                  id="vatOption"
                  value={vatOptions.find((opt) => opt.value === vatOption)}
                  onChange={(selected) => setVatOption(selected.value)}
                  options={vatOptions}
                  styles={selectStyles}
                  classNamePrefix="react-select"
                />
              </div>
              {vatOption !== "non-vat" && (
                <>
                  <div>
                    <label htmlFor="vatMethod" className="block text-sm">
                      {vatOption === "tax" ? "Tax" : "VAT"} Type
                    </label>
                    <Select
                      id="vatMethod"
                      value={vatMethodOptions.find(
                        (opt) => opt.value === vatMethod
                      )}
                      onChange={(selected) => setVatMethod(selected.value)}
                      options={vatMethodOptions}
                      styles={selectStyles}
                      classNamePrefix="react-select"
                    />
                  </div>
                  {vatOption === "tax" && (
                    <>
                      <div>
                        <label htmlFor="taxCode" className="block text-xs">
                          Tax Code
                        </label>
                        <input
                          id="taxCode"
                          typeof="text"
                          value={taxCode}
                          onChange={(e) => setTaxCode(e.target.value)}
                          className="custom-input"
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <label htmlFor="vatRate" className="block text-sm">
                      {vatOption === "tax" ? "Tax" : "Vat"} Rate (%)
                    </label>
                    <input
                      id="vatRate"
                      type="number"
                      value={vatRate}
                      onChange={(e) => setVatRate(e.target.value)}
                      className="custom-input"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Company Logo */}
          <div className="flex items-start gap-6 bg-white/10 p-4 rounded-lg">
            <div className="flex-1">
              <label htmlFor="logo" className="block text-sm">
                Company Logo
              </label>
              {/* <input
              id="logo"
              type="file"
              accept="image/*"
              ref={logoInputRef}
              onClick={(e) => {
                e.currentTarget.value = null;
              }}
              onChange={(e) => handleFileChange(e, "logo")}
              className="w-full file-input"
            /> */}
              <button
                type="button"
                onClick={() => handleImageFieldClick("logo")}
                className="w-full p-3 border rounded-lg custom-input text-left hover:bg-white/5"
              >
                {logo ? "Change Image" : "Select Image"}
              </button>

              {logoDims.width > 0 && (
                <p className="mt-1 text-xs text-center text-gray-200">
                  Dimensions: {logoDims.width}px × {logoDims.height}
                  px
                </p>
              )}
            </div>
            {(previews.logoPreview || logo) && (
              <div className="flex flex-col items-center gap-2">
                <img
                  src={previews.logoPreview || formatOldUrl(logo)}
                  alt="Company Logo Preview"
                  className="h-20 w-auto border dark:border-gray-600 rounded-lg shadow-md"
                  onLoad={(e) =>
                    setLogoDims({
                      width: e.currentTarget.naturalWidth,
                      height: e.currentTarget.naturalHeight,
                    })
                  }
                />

                <button
                  type="button"
                  onClick={() => {
                    setPreviews((prev) => ({ ...prev, logoPreview: null }));
                    setLogo(null);
                    if (logoInputRef.current) {
                      logoInputRef.current.value = "";
                    }
                    setLogoDims({
                      width: 0,
                      height: 0,
                    });
                  }}
                  className="bg-red-400 dark:bg-red-500 text-color text-sm px-2 py-1 rounded hover:bg-red-500 dark:hover:bg-red-600"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* Company Information */}
          <div className="bg-white/10 p-4 rounded-lg space-y-4">
            <h3 className="font-bold text-lg">Issuing Company</h3>

            <div className="flex flex-row w-full space-x-2">
              <div className="flex-1">
                <label htmlFor="from-name" className="block text-sm">
                  Company Name
                </label>
                <input
                  id="from-name"
                  type="text"
                  value={from.name}
                  onChange={(e) =>
                    setFrom((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full p-2 border rounded-lg custom-input"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="from-contact" className="block text-sm">
                  Contact
                </label>
                <input
                  id="from-contact"
                  type="text"
                  value={from.contact}
                  onChange={(e) =>
                    setFrom((prev) => ({ ...prev, contact: e.target.value }))
                  }
                  className="w-full p-2 border rounded-lg custom-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="from-address" className="block text-sm">
                Address
              </label>
              <input
                id="from-address"
                type="text"
                value={from.address}
                onChange={(e) =>
                  setFrom((prev) => ({ ...prev, address: e.target.value }))
                }
                className="w-full p-2 border rounded-lg custom-input"
              />
            </div>
          </div>

          {/* Payment Details */}
          <div className="bg-white/10 p-4 rounded-lg space-y-4">
            <h3 className="font-bold text-lg">Payment Details</h3>

            <div>
              <label htmlFor="cashDetails" className="block text-sm">
                Cash Details{"  "}
                <span className="text-gray-400">
                  (Press <kbd>Enter</kbd> / <kbd>Return</kbd> to separate
                  multiple entries)
                </span>
              </label>

              <textarea
                id="cashDetails"
                ref={cashDetailsRef}
                value={cash}
                onChange={(e) => {
                  setCash(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                className="w-full border rounded px-4 py-2 resize-none overflow-hidden custom-input"
                placeholder="Enter cash payment details"
                rows="1"
              />
            </div>

            <div>
              <label htmlFor="checkDetails" className="block text-sm">
                Check Details{"  "}
                <span className="text-gray-400">
                  (Press <kbd>Enter</kbd> / <kbd>Return</kbd> to separate
                  multiple entries)
                </span>
              </label>
              <textarea
                id="checkDetails"
                ref={checkDetailsRef}
                value={check}
                onChange={(e) => {
                  setCheck(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                className="w-full border rounded px-4 py-2 resize-none overflow-hidden custom-input"
                placeholder="Enter check payment details"
                rows="1"
              />
            </div>

            {/* Gcash QR */}
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <label htmlFor="qr" className="block text-sm">
                  Gcash QR Code
                </label>
                {/* <input
                id="qr"
                type="file"
                accept="image/*"
                ref={gcashQrInputRef}
                onClick={(e) => {
                  e.currentTarget.value = null;
                }}
                onChange={(e) => handleFileChange(e, "gcashQr")}
                className="w-full file-input"
              /> */}
                <button
                  type="button"
                  onClick={() => handleImageFieldClick("gcashQr")}
                  className="w-full p-3 border rounded-lg custom-input text-left hover:bg-white/5"
                >
                  {qr ? "Change Image" : "Select Image"}
                </button>

                {qrDims.width > 0 && (
                  <p className="mt-1 text-xs text-center text-gray-200">
                    Dimensions: {qrDims.width}px × {qrDims.height}
                    px
                  </p>
                )}
              </div>

              {(previews.qrPreview || qr) && (
                <div className="flex flex-col items-center gap-2">
                  <img
                    src={previews.qrPreview || formatOldUrl(qr)}
                    alt="Gcash QR Preview"
                    className="w-28 h-auto border dark:border-gray-600 rounded-md shadow-sm"
                    onLoad={(e) =>
                      setQrDims({
                        width: e.currentTarget.naturalWidth,
                        height: e.currentTarget.naturalHeight,
                      })
                    }
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPreviews((prev) => ({ ...prev, qrPreview: null }));
                      setQr(null);
                      if (gcashQrInputRef.current) {
                        gcashQrInputRef.current.value = "";
                      }
                      setQrDims({
                        width: 0,
                        height: 0,
                      });
                    }}
                    className="bg-red-400 dark:bg-red-500 text-color text-sm px-2 py-1 rounded hover:bg-red-500 dark:hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            {/* Gcash QR 2 */}
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <label htmlFor="qr2" className="block text-sm">
                  Alternative Gcash QR Code
                </label>
                {/* <input
                id="qr2"
                type="file"
                accept="image/*"
                ref={gcashQr2InputRef}
                onClick={(e) => {
                  e.currentTarget.value = null;
                }}
                onChange={(e) => handleFileChange(e, "gcashQr2")}
                className="w-full file-input"
              /> */}
                <button
                  type="button"
                  onClick={() => handleImageFieldClick("gcashQr2")}
                  className="w-full p-3 border rounded-lg custom-input text-left hover:bg-white/5"
                >
                  {qr2 ? "Change Image" : "Select Image"}
                </button>

                {qr2Dims.width > 0 && (
                  <p className="mt-1 text-xs text-center text-gray-200">
                    Dimensions: {qr2Dims.width}px × {qr2Dims.height}
                    px
                  </p>
                )}
              </div>

              {(previews.qrPreview2 || qr2) && (
                <div className="flex flex-col items-center gap-2">
                  <img
                    src={previews.qrPreview2 || formatOldUrl(qr2)}
                    alt="Gcash QR 2 Preview"
                    className="w-28 h-auto border dark:border-gray-600 rounded-md shadow-sm"
                    onLoad={(e) =>
                      setQr2Dims({
                        width: e.currentTarget.naturalWidth,
                        height: e.currentTarget.naturalHeight,
                      })
                    }
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPreviews((prev) => ({ ...prev, qrPreview2: null }));
                      setQr2(null);
                      if (gcashQr2InputRef.current) {
                        gcashQr2InputRef.current.value = "";
                      }
                      setQr2Dims({
                        width: 0,
                        height: 0,
                      });
                    }}
                    className="bg-red-400 dark:bg-red-500 text-color text-sm px-2 py-1 rounded hover:bg-red-500 dark:hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            <div className="flex bg-white/10 py-4 rounded-lg justify-center items-center gap-4">
              {!(previews.alignedQRsPreview || alignedQRs) && (
                <button
                  type="button"
                  className="btn-action"
                  onClick={() => setShowQRAlignmentModal(true)}
                  disabled={!qr || !qr2}
                >
                  {qrAlignment ? "Re-align QR Codes" : "Align QR Codes"}
                  {(!qr || !qr2) && " (Upload both QRs first)"}
                </button>
              )}

              {/* Aligned QR Preview */}
              {(previews.alignedQRsPreview || alignedQRs) && (
                <div className="flex  flex-col items-center gap-2">
                  <label className="block text-sm font-medium">
                    Aligned QR Codes Preview
                  </label>
                  <img
                    src={previews.alignedQRsPreview || formatOldUrl(alignedQRs)}
                    alt="Aligned QR Codes Preview"
                    className="max-h-40 w-auto border rounded shadow-sm"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setPreviews((prev) => ({
                          ...prev,
                          alignedQRsPreview: null,
                        }));

                        setAlignedQRs(null);
                        setQrAlignment(null);
                      }}
                      className="bg-red-400 dark:bg-red-500 text-color text-sm px-2 py-1 rounded hover:bg-red-500 dark:hover:bg-red-600"
                    >
                      Remove Aligned QRs
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowQRAlignmentModal(true)}
                      className="bg-blue-400 text-color text-sm px-2 py-1 rounded hover:bg-blue-500"
                    >
                      Re-align
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 p-4 border-t border-gray-300 dark:border-gray-600 bg-tier-primary dark:bg-tier-primary-dark">
          <button onClick={onClose} className="btn-action-white">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-action">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditTemplateModal;
